import { useState } from "react";
import { toast } from "react-toastify";

import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";

import { Button, Grid2, Stack, Typography } from "@mui/material";
import { IconDownload, IconFilter, IconUserPlus } from "@tabler/icons-react";

import ContainerLoading from "../components/ContainerLoading";
import IndicatorsCard from "../components/IndicatorsCard";
import StudentTableComponent from "../components/StudentTableComponent";
import StudentRegister from "../components/StudentRegister";
import StudentFilters from "../components/StudentFilters";
import StudentReportDownload from "../components/StudentReportDownload";

import {
  downloadListStudentPDF,
  downloadListStudentToCSV,
  studentsIndicators,
  studentsList,
} from "../services/students";
import PageHeader from "../components/PageHeader";

const Students = () => {
  const [open, setOpen] = useState(false);
  const [openDownloadButton, setOpenDownloadButton] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({
    financeStatus: "",
    fightId: "",
    graduation: "",
    nextGraduation: "",
  });
  const hasFilters = Object.values(filters).some((value) => value !== "");

  const onFilter = (e: {
    financeStatus: string;
    fightId: string;
    graduation: string;
    nextGraduation: string;
  }) => {
    setFilters(e);
    setOpenFilters(false);
    refetch();
  };

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  }: {
    data: Array<{
      id: string;
      fullName: string;
      email: string;
      phone: string;
      createdAt: string | Date;
      status: string;
      payment: [];
      studentOnPlan: [];
    }>;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["studentsList" + JSON.stringify(filters)],
    queryFn: async () =>
      await studentsList({
        ...filters,
      }),
    initialData: [],
  });

  const {
    data: dataIndicators,
    isLoading: isLoadingIndicators,
    isFetching: isFetchingIndicators,
    refetch: refetchIndicators,
  }: {
    data: {
      allStudents: number;
      studentsByMonth: number;
      absentStudents: number;
      studentsPresents: number;
    };
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["studentsIndicators"],
    queryFn: async () =>
      await studentsIndicators({
        startDate: moment().startOf("month").toDate(),
        endDate: moment().endOf("month").toDate(),
      }),
    initialData: {
      allStudents: 0,
      studentsByMonth: 0,
      absentStudents: 0,
      studentsPresents: 0,
    },
  });

  const { mutate: mutateDownloadListStudentToCSV, isPending } = useMutation({
    mutationFn: downloadListStudentToCSV,
    onSuccess: () => {
      toast.success("CSV gerado com sucesso");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const {
    mutate: mutateDownloadListStudentPDF,
    isPending: isPendingDownloadListStudentPDF,
  } = useMutation({
    mutationFn: downloadListStudentPDF,
    onSuccess: () => {
      toast.success("PDF gerado com sucesso");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const refetchAll = () => {
    refetch();
    refetchIndicators();
  };

  const loading =
    isPending ||
    isLoading ||
    isFetching ||
    isLoadingIndicators ||
    isFetchingIndicators ||
    isPendingDownloadListStudentPDF;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Alunos"
          subtitle="Informações gerais dos seus alunos."
          options={
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{
                alignItems: { xs: "flex-start", lg: "center" },
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{ gap: 0.5 }}
                onClick={() => setOpenDownloadButton(true)}
              >
                <IconDownload size={18} />
                <Typography variant="body2" fontSize={{ xs: 12 }}>
                  Baixar relatório
                </Typography>
              </Button>
              <Button
                variant="contained"
                sx={{ gap: 0.5 }}
                onClick={() => setOpen(true)}
              >
                <IconUserPlus size={18} />
                <Typography variant="body2" fontSize={{ xs: 12 }}>
                  Cadastrar aluno
                </Typography>
              </Button>

              <Button
                variant="contained"
                sx={{
                  gap: 0.5,
                  backgroundColor: hasFilters
                    ? "primary.main"
                    : "secondary.main",
                }}
                onClick={() => setOpenFilters(true)}
              >
                <IconFilter size={18} />
                <Typography variant="body2">Filtros</Typography>
              </Button>
            </Stack>
          }
        />

        <Grid2 size={12}>
          <IndicatorsCard
            data={[
              {
                id: 2,
                title: "Novos no mês",
                value: dataIndicators?.studentsByMonth,
                icon: "icon_school",
                size: { xs: 12, sm: 4, md: 4 },
              },
              {
                id: 3,
                title: "Ausentes no mês",
                value: dataIndicators?.absentStudents,
                icon: "icon_school",
                size: { xs: 12, sm: 4, md: 4 },
              },
              {
                id: 1,
                title: "Check-in no mês",
                value: dataIndicators?.studentsPresents,
                icon: "icon_school",
                size: { xs: 12, sm: 4, md: 4 },
              },
            ]}
          />
        </Grid2>
        <Grid2 size={12}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <StudentTableComponent data={data} refetch={() => refetchAll()} />
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
      {open && (
        <StudentRegister
          open={open}
          onClose={() => setOpen(false)}
          refetch={() => refetchAll()}
        />
      )}
      <StudentFilters
        open={openFilters}
        onClose={() => setOpenFilters(false)}
        onFilter={onFilter}
      />
      <StudentReportDownload
        open={openDownloadButton}
        onClose={() => setOpenDownloadButton(false)}
        onSelectCSV={() => {
          mutateDownloadListStudentToCSV();
          setOpenDownloadButton(false);
        }}
        onSelectPDF={() => {
          mutateDownloadListStudentPDF();
          setOpenDownloadButton(false);
        }}
      />
    </ContainerLoading>
  );
};

export default Students;
